<script lang="ts">
    export interface Issue {
        IssueId: string;
        Title: string;
        Status: number;
        Subject?: string;
    }

    export interface Journey {
        JourneyId: string;
        Description: string;
        GraphicalData: string;
        Title: string;
        LastModified: string;
        LastModifiedBy: {
            FirstName: string;
            LastName: string;
            Name: string;
            UserId: string;
            UserName: string;
        };
        Status: number;
        Tags: {
            TagId: string;
            Title: string;
        }[];
        data: any;
        ClickedCount: number;
        ClickedPercentage: number;
        ContactCount: number;
        OpenedCount: number;
        OpenedPercentage: number;
        RecipientCount: number;
        index: number;
        Issues?: Issue[];
    }
</script>

<script lang="ts" setup>
    import TagsList from '@shared/components/Tags/TagsList.vue';
    import HumanTime from '@shared/utils/HumanTime/HumanTime';
    import JourneyPreview from '../Pages/JourneyPreview.vue';
    import { type TagsProvide } from '@shared/components/Tags/Tags.access';
    import Alert from '@shared/components/BsAlert.vue';

    const toHumanTime = new HumanTime({ locale: 'sv' });

    const props = defineProps<{
        journey: Journey;
        index: number;
        tags?: TagsProvide;
        currentTab?: string;
        hideStats?: boolean;
    }>();
</script>

<template>
    <div class="card-media">
        <JourneyPreview :index="index" :key="props.journey.JourneyId" :journey="props.journey" @click.prevent="$emit('journeyClicked')" />
    </div>
    <div class="flex-1 card-content">
        <h4>
            <a :href="'/journeys/' + props.journey.JourneyId" @click.prevent="$emit('journeyClicked')">{{ props.journey.Title || $Resources.NoTitleLabel }}</a>
        </h4>

        <p v-if="props.journey.Description">
            {{ props.journey.Description }}
        </p>

        <!-- :mode="startMenu.selected[journey.JourneyId] ? 'edit' : 'display'"  -->

        <TagsList
            v-if="props.tags && props.journey.data.Tags.length > 0"
            :tags="props.journey.data.Tags"
            :selectedIds="props.tags.state.selectedIds"
            @toggleTag="(tag) => props.tags!.toggle(tag)"></TagsList>

        <p class="meta">
            <span class="glyphicon glyphicon-time m-xs-r"></span>
            <time :datetime="props.journey.LastModified">{{ toHumanTime.parse(props.journey.LastModified) }}</time>
            <span class="glyphicon glyphicon-user m-xs-r"></span>
            {{ props.journey.LastModifiedBy.Name }}
        </p>

        <Alert
            v-if="props.currentTab !== 'drafts' && props.journey.Issues?.find((i) => i.Status === 60)"
            class="m-sm-t m-xs-b"
            :type="'warning'"
            :title="$Resources.JourneyHasAbortedIssuesAlertTitle"
            :content="$Resources.JourneyHasAbortedIssuesAlertContent"
            :buttonText="$Resources.JourneyHasAbortedIssuesAlertButton"
            :dismissible="false"
            @buttonClick="$emit('reactivateAbortedIssuesClicked', props.journey.Issues)"></Alert>
    </div>
    <div v-if="props.currentTab !== 'drafts' && !hideStats" class="card-content">
        <div class="d-flex col-gap-md justify-content-between flex-wrap ug-bars text-center">
            <div class="bar-label">
                {{ $Resources.JourneyListStatsContactsLabel }}
                <span>
                    {{ props.journey.ContactCount }}
                </span>
            </div>
            <div class="bar-label">
                {{ $Resources.JourneyListStatsRecipientsLabel }}
                <span>
                    {{ props.journey.RecipientCount }}
                </span>
            </div>
            <div>
                <div class="bar-label">
                    {{ $Resources.JourneyListStatsOpenLabel }}
                    <span>{{ Math.round(props.journey.OpenedPercentage) }}%</span>
                </div>
                <div class="bar">
                    <span class="bar-value bg-opened" :style="{ width: Math.round(props.journey.OpenedPercentage) + '%' }"></span>
                </div>
            </div>
            <div>
                <div class="bar-label">
                    {{ $Resources.JourneyListStatsClickedLabel }}
                    <span>{{ Math.round(props.journey.ClickedPercentage) }}%</span>
                </div>
                <div class="bar">
                    <span class="bar-value bg-clicked" :style="{ width: Math.round(props.journey.ClickedPercentage) + '%' }"></span>
                </div>
            </div>
        </div>
    </div>
</template>

<style></style>
